import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import {
  Form,
  minLength,
  required,
  useDataProvider,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState, type ReactNode } from "react";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { fullScreenOnMobile } from "../layout/mobileDialog";
import type { CrmDataProvider } from "../providers/types";

export const PASSWORD_MIN_LENGTH = 8;

type ChangePasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const confirmMatches = (
  value: string,
  allValues: Partial<ChangePasswordFormValues>,
) =>
  value !== allValues.newPassword ? "crm.profile.password.mismatch" : undefined;

/**
 * "Change password" button that opens a dialog form (current password, new
 * password, confirmation) and calls `dataProvider.changePassword`.
 *
 * Pass a custom `trigger` to restyle the button (e.g. full-width on mobile).
 */
export const ChangePasswordDialog = ({ trigger }: { trigger?: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { mutate, isPending } = useMutation({
    mutationKey: ["changePassword"],
    mutationFn: async ({
      currentPassword,
      newPassword,
    }: ChangePasswordFormValues) =>
      dataProvider.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setOpen(false);
      notify("crm.profile.password.changed");
    },
    onError: (error: Error) => {
      notify(error?.message || translate("crm.profile.password.error"), {
        type: "error",
      });
    },
  });

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    mutate(values as ChangePasswordFormValues);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" type="button">
            <KeyRound />
            {translate("crm.profile.password.change")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className={`md:max-w-md ${fullScreenOnMobile}`}>
        <DialogHeader>
          <DialogTitle>{translate("crm.profile.password.change")}</DialogTitle>
          <DialogDescription>
            {translate("crm.profile.password.description")}
          </DialogDescription>
        </DialogHeader>
        <Form onSubmit={handleSubmit} className="space-y-4">
          <TextInput
            source="currentPassword"
            type="password"
            autoComplete="current-password"
            label="crm.profile.password.current"
            validate={required()}
            helperText={false}
          />
          <TextInput
            source="newPassword"
            type="password"
            autoComplete="new-password"
            label="crm.profile.password.new"
            validate={[
              required(),
              minLength(PASSWORD_MIN_LENGTH, "crm.profile.password.too_short"),
            ]}
            helperText={false}
          />
          <TextInput
            source="confirmPassword"
            type="password"
            autoComplete="new-password"
            label="crm.profile.password.confirm"
            validate={[required(), confirmMatches]}
            helperText={false}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {translate("ra.action.save")}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
